import React from 'react';
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

const Button = React.forwardRef(({
    className,
    variant = 'default',
    size = 'default',
    children,
    loading = false,
    fullWidth = false,
    disabled = false,
    type = 'button',
    iconName,
    iconPosition = 'left',
    ...props
}, ref) => {
    // Base button classes — no rounded here; applied conditionally below
    const baseClasses = "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
    
    // Variant classes — all use CSS theme variables
    const variantClasses = {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-transparent hover:bg-muted/50 text-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted/50 text-foreground hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-success text-success-foreground hover:bg-success/90",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90",
        danger: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
    };
    
    // Size classes — no rounded here; applied conditionally below
    const sizeClasses = {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
        xs: "h-8 px-2 text-xs",
        xl: "h-12 px-10 text-base",
    };

    // Only apply default rounded-md when the caller has NOT passed a rounded-* override
    const hasRoundedOverride = className && /\brounded-/?.test(className);
    const defaultRounded = hasRoundedOverride ? '' : 'rounded-md';

    // Loading spinner
    const LoadingSpinner = () => (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const iconSize = size === 'sm' || size === 'xs' ? 14 : 16;

    return (
        <button
            className={cn(
                baseClasses,
                defaultRounded,
                variantClasses?.[variant] || variantClasses?.default,
                sizeClasses?.[size] || sizeClasses?.default,
                fullWidth && "w-full",
                className
            )}
            ref={ref}
            disabled={disabled || loading}
            type={type}
            {...props}
        >
            {loading && <LoadingSpinner />}
            {iconName && iconPosition === 'left' && !loading && (
                <Icon name={iconName} size={iconSize} className="mr-1.5 flex-shrink-0" />
            )}
            {children}
            {iconName && iconPosition === 'right' && !loading && (
                <Icon name={iconName} size={iconSize} className="ml-1.5 flex-shrink-0" />
            )}
        </button>
    );
});

Button.displayName = "Button";
export default Button;